import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Grid,
  TextField,
  IconButton,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Alert,
  Chip
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccountBalanceWallet as WalletIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  MoreVert as MoreVertIcon,
  Payment as PaymentIcon,
  Book as LedgerIcon,
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { customerService } from '../../services/customerService';
import { enqueueSnackbar } from 'notistack';
import axios from 'axios';
import { getAuthHeader } from '../../services/authService';
import API_CONFIG from '../../config';
import creditService from '../../services/creditService';
import dashboardService from '../../services/dashboardService';
import * as XLSX from 'xlsx';
import { formatCurrency as formatCurrencyGlobal, getCurrencySymbol } from '../../utils/currencyFormatter';

// Helper function to format a date string or return a default value if invalid
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';

  try {
    // Try to parse the date
    const date = new Date(dateString);

    // Check if the date is valid
    if (date instanceof Date && !isNaN(date)) {
      // Format as local date string
      return date.toLocaleDateString();
    }

    return 'N/A';
  } catch (error) {
    console.error('Error formatting date:', error, dateString);
    return 'N/A';
  }
};

// Helper to get created date from customer object, checking all possible field names
const getCustomerCreatedDate = (customer) => {
  // Log the entire customer object to debug
  console.log('Customer object:', customer);

  // Try all possible field names
  return customer.createdAt || customer.created_at;
};

// Helper function to format currency values safely
const formatCurrency = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return formatCurrencyGlobal(0);
  }
  return formatCurrencyGlobal(value);
};

const CreditCustomersReport = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalCreditAmount, setTotalCreditAmount] = useState(0);
  const [activeCreditCustomersCount, setActiveCreditCustomersCount] = useState(0);

  // For dropdown action menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);

  // For payment dialog
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [paymentNote, setPaymentNote] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [creditSalesData, setCreditSalesData] = useState({
    totalSale: 0,
    totalPaid: 0,
    openingBalance: 0,
    currentBalance: 0,
    transactions: []
  });
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [error, setError] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  // New state variables for "View All Transactions" feature
  const [allTransactionsDialogOpen, setAllTransactionsDialogOpen] = useState(false);
  const [allCustomersWithHistory, setAllCustomersWithHistory] = useState([]);
  const [selectedHistoryCustomer, setSelectedHistoryCustomer] = useState(null);
  const [loadingAllCustomers, setLoadingAllCustomers] = useState(false);
  const [showingHistoryLedger, setShowingHistoryLedger] = useState(false);
  const [historySearchTerm, setHistorySearchTerm] = useState(''); // Search for all transactions dialog

  const navigate = useNavigate();

  // Fetch credit customers from the API using centralized service
  const fetchCustomers = async () => {
    try {
      setLoading(true);

      // Use the centralized dashboard service for consistency
      const creditData = await dashboardService.getCreditCustomersData();

      setCustomers(creditData.customers);
      setTotalCreditAmount(creditData.totalDueAmount);
      setActiveCreditCustomersCount(creditData.totalCreditCustomers);

      console.log('📊 Credit Customers Report Results (using centralized service):');
      console.log('   Customers with positive balance due:', creditData.totalCreditCustomers);
      console.log('   Total balance due:', creditData.totalDueAmount);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching customers:', error);
      enqueueSnackbar('Failed to load customer data', { variant: 'error' });
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleRefresh = () => {
    fetchCustomers();
  };

  // Action menu handlers
  const handleOpenMenu = (event, customerId) => {
    setAnchorEl(event.currentTarget);
    setSelectedCustomerId(customerId);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedCustomerId(null);
  };

  const handlePayAction = () => {
    // Find the selected customer
    const customer = customers.find(c => c.id === selectedCustomerId);
    if (customer) {
      setSelectedCustomer(customer);
      setError(null); // Clear any previous errors

      // Set credit sales data with initial values from customer's balance due
      setCreditSalesData({
        totalSale: 0,
        totalPaid: 0,
        openingBalance: 0,
        currentBalance: parseFloat(customer.balanceDue || 0),
        transactions: []
      });

      // Set default payment fields
      setPaymentAmount(customer.balanceDue ? customer.balanceDue.toString() : '0');
      setPaymentDate(new Date().toISOString().split('T')[0]);
      setPaymentMethod('Cash');
      setPaymentNote('');

      // Open dialog immediately with initial data
      setPaymentDialogOpen(true);

      // Fetch additional data asynchronously (pass customer so fallback works even before state settles)
      fetchCustomerCreditData(customer.id, customer);
    }
    handleCloseMenu();
  };

  const handleClosePaymentDialog = () => {
    setPaymentDialogOpen(false);
    setSelectedCustomer(null);
    setPaymentAmount('');
  };

  const handlePaymentAmountChange = (event) => {
    const value = event.target.value;
    // Only allow numeric input with up to 2 decimal places
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setPaymentAmount(value);
    }
  };

  const handleSubmitPayment = async () => {
    if (!selectedCustomer) return;

    try {
      setProcessingPayment(true);
      setError(null);

      const paymentData = {
        amount: parseFloat(paymentAmount),
        paymentMethod: paymentMethod,
        notes: paymentNote,
        referenceId: 'MANUAL-PAYMENT-' + new Date().getTime(), // Add reference ID for manual payments
        transactionDate: new Date(paymentDate).toISOString()
      };

      await creditService.processPayment(selectedCustomer.id, paymentData);

      // Show success message
      enqueueSnackbar('Payment processed successfully', { variant: 'success' });

      // Refresh data
      handleClosePaymentDialog();
      fetchCustomers();
    } catch (err) {
      console.error('Error processing payment:', err);
      setError(err.message || 'Failed to process payment. Please try again.');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleLedgerAction = () => {
    console.log('Ledger action for customer ID:', selectedCustomerId);
    // Navigate to the customer ledger page with the customer ID
    navigate(`/customer-credit-ledger/${selectedCustomerId}`);
    handleCloseMenu();
  };

  const fetchCustomerCreditData = async (customerId, customerObj) => {
    try {
      setError(null);

      const [summary, transactions] = await Promise.all([
        creditService.getCustomerCreditSummary(customerId),
        creditService.getCustomerTransactions(customerId)
      ]);

      if (summary) {
        // Apply fallback: if backend summary balanceDue=0 but customer creditBalance>0
        // (happens for split-bill partial credit sales), use creditBalance as the true balance
        const summaryBalanceDue = parseFloat(summary.balanceDue || 0);
        const customerCreditBal = parseFloat(
          (customerObj || selectedCustomer)?.creditBalance ||
          (customerObj || selectedCustomer)?.credit_balance ||
          0
        );
        // customers.credit_balance is the authoritative source — always updated correctly by backend.
        // The transaction running_balance can go negative after payments (backend bug).
        // Use creditBalance when positive; fall back to max(0, summaryBalanceDue) otherwise.
        const effectiveBalanceDue = customerCreditBal > 0 ? customerCreditBal : Math.max(0, summaryBalanceDue);

        const updatedCreditData = {
          totalSale: summary.totalCreditSales || 0,        // Total from credit sales orders
          totalPaid: summary.totalPayments || 0,           // Sum of all payments
          openingBalance: summary.openingBalance || 0,     // Opening balance from customers table
          currentBalance: effectiveBalanceDue,             // Use effective balance (with fallback)
          transactions: transactions || []
        };

        setCreditSalesData(updatedCreditData);

        // Update the payment amount to reflect the correct balance due
        setPaymentAmount(effectiveBalanceDue.toString());

        console.log('📊 Fresh credit data loaded:', {
          customerId,
          balanceDueFromAPI: summaryBalanceDue,
          customerCreditBalance: customerCreditBal,
          effectiveBalanceDue,
          openingBalance: summary.openingBalance,
          totalCreditSales: summary.totalCreditSales,
          totalPayments: summary.totalPayments
        });
      }
    } catch (err) {
      console.error('Error fetching credit data:', err);
      setError('Failed to load customer credit data. Please try again.');
    }
  };

  // New handlers for "View All Transactions" feature
  const handleViewAllTransactions = async () => {
    try {
      setLoadingAllCustomers(true);
      setAllTransactionsDialogOpen(true);

      const allCustomers = await creditService.getAllCustomersWithCreditHistory();
      setAllCustomersWithHistory(allCustomers);

      console.log('📊 All Customers with Credit History:', allCustomers.length);
    } catch (error) {
      console.error('Error fetching customers with credit history:', error);
      enqueueSnackbar('Failed to load customers with credit history', { variant: 'error' });
    } finally {
      setLoadingAllCustomers(false);
    }
  };

  const handleCloseAllTransactionsDialog = () => {
    setAllTransactionsDialogOpen(false);
    setSelectedHistoryCustomer(null);
    setShowingHistoryLedger(false);
    setAllCustomersWithHistory([]);
    setHistorySearchTerm(''); // Reset search when closing
  };

  const handleSelectHistoryCustomer = (customer) => {
    setSelectedHistoryCustomer(customer);
    setShowingHistoryLedger(true);
  };

  const handleBackToCustomerList = () => {
    setSelectedHistoryCustomer(null);
    setShowingHistoryLedger(false);
  };

  const handleHistorySearchChange = (event) => {
    setHistorySearchTerm(event.target.value);
  };

  const handleExportToExcel = async () => {
    try {
      setIsExporting(true);
      // Prepare export data from customers
      const exportData = customers.map((cust, idx) => ({
        'S.No': idx + 1,
        'Customer Name': cust.name,
        'Phone': cust.phone,
        'Email': cust.email,
        'Balance Due': cust.balanceDue,
        'Total Returns': cust.totalReturns,
        'Created At': formatDate(cust.createdAt)
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);
      ws['!cols'] = [
        { wch: 5 }, { wch: 28 }, { wch: 15 }, { wch: 25 }, { wch: 15 }, { wch: 15 }, { wch: 18 }
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.sheet_add_aoa(ws, [
        ['Credit Customers Report'],
        ['Generated on: ' + new Date().toLocaleString()],
        ['']
      ], { origin: 'A1' });
      XLSX.utils.book_append_sheet(wb, ws, 'Credit Customers');
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const fileData = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(fileData);
      const link = document.createElement('a');
      link.href = url;
      link.download = `credit_customers_report_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      enqueueSnackbar && enqueueSnackbar('Credit customers report exported successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar && enqueueSnackbar('Failed to export credit customers report', { variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  };

  const handleDebugCreditCustomers = async () => {
    try {
      setLoading(true);
      console.log('🔍 Starting debug analysis...');

      // Get all customers with credit history
      const allCustomers = await creditService.getAllCustomersWithCreditHistory();
      console.log('📊 All customers with credit history:', allCustomers.length);

      // Use the centralized dashboard service for comparison
      const creditData = await dashboardService.getCreditCustomersData();
      console.log('📊 Customers showing in main report:', creditData.totalCreditCustomers);

      // Compare the two datasets
      console.log('🔍 DETAILED COMPARISON:');
      console.log('----------------------------------------');

      allCustomers.forEach(customer => {
        const isInMainReport = creditData.customers.find(c => c.id === customer.id);
        console.log(`Customer: ${customer.name} (ID: ${customer.id})`);
        console.log(`  - Credit Balance: ${customer.creditBalance}`);
        console.log(`  - Opening Balance: ${customer.openingBalance}`);
        console.log(`  - Total Credit Sales: ${customer.totalCreditSales}`);
        console.log(`  - Total Payments: ${customer.totalPayments}`);
        console.log(`  - Total Returns: ${customer.totalReturns}`);
        console.log(`  - Balance Due: ${customer.balanceDue}`);
        console.log(`  - In Main Report: ${isInMainReport ? 'YES' : 'NO'}`);
        console.log(`  - Reason for exclusion: ${customer.balanceDue <= 0 ? 'Balance Due is zero or negative' : 'Should be included'}`);
        console.log('----------------------------------------');
      });

      // Summary
      const customersWithPositiveBalance = allCustomers.filter(c => c.balanceDue > 0);
      const customersWithZeroBalance = allCustomers.filter(c => c.balanceDue <= 0);

      console.log('🎯 SUMMARY:');
      console.log(`Total customers with transaction history: ${allCustomers.length}`);
      console.log(`Customers with positive balance due: ${customersWithPositiveBalance.length}`);
      console.log(`Customers with zero/negative balance due: ${customersWithZeroBalance.length}`);

      // Show alert with summary
      alert(`Debug Results:
Total customers with credit history: ${allCustomers.length}
Customers with positive balance (showing in report): ${customersWithPositiveBalance.length}
Customers with zero/negative balance (filtered out): ${customersWithZeroBalance.length}

Check console for detailed breakdown.`);

    } catch (error) {
      console.error('Error in debug analysis:', error);
      alert('Error running debug analysis. Check console for details.');
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.phone.includes(searchTerm) ||
    (customer.email && customer.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Filter customers with history based on search term
  const filteredHistoryCustomers = allCustomersWithHistory.filter(customer =>
    customer.name.toLowerCase().includes(historySearchTerm.toLowerCase())
  );

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Credit Customers Management
          </Typography>
        </Box>

        {/* Export Button Row */}
        <Grid container justifyContent="flex-end" sx={{ mb: 2 }}>
          <Grid item sx={{ mr: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleViewAllTransactions}
              startIcon={<AssignmentIcon />}
              sx={{ minWidth: 180, height: '48px', fontWeight: 600, display: 'none' }}
            >
              View All Transactions
            </Button>
          </Grid>
          <Grid item sx={{ mr: 2 }}>
            <Button
              variant="outlined"
              color="warning"
              onClick={handleDebugCreditCustomers}
              startIcon={<AssignmentIcon />}
              sx={{ minWidth: 180, height: '48px', fontWeight: 600, display: 'none' }}
            >
              Debug Credit Data
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleExportToExcel}
              disabled={isExporting || customers.length === 0}
              startIcon={isExporting ? <CircularProgress size={20} /> : <FileDownloadIcon />}
              sx={{ minWidth: 180, height: '48px', fontWeight: 600 }}
            >
              {isExporting ? 'Exporting...' : 'Export to Excel'}
            </Button>
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6} lg={3}>
            <Card>
              <CardContent>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <WalletIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6">Total Balance Due</Typography>
                    </Box>
                    <Typography variant="h4">₹ {totalCreditAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Card>
              <CardContent>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PersonIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6">Credit Customers</Typography>
                    </Box>
                    <Typography variant="h4">{activeCreditCustomersCount}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Customers with transaction history
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Card>
              <CardContent>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <WalletIcon color="success" sx={{ mr: 1 }} />
                      <Typography variant="h6">Positive Balance</Typography>
                    </Box>
                    <Typography variant="h4">{customers.filter(c => parseFloat(c.balanceDue || 0) > 0).length}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Customers owing money
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Card>
              <CardContent>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <WalletIcon color="error" sx={{ mr: 1 }} />
                      <Typography variant="h6">Zero/Paid Balance</Typography>
                    </Box>
                    <Typography variant="h4">{customers.filter(c => parseFloat(c.balanceDue || 0) <= 0).length}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Customers with no debt
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper sx={{ mb: 3, p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                label="Search Customers"
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  ),
                }}
                placeholder="Name, Phone, or Email"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={8} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                sx={{ mr: 1 }}
                disabled={loading}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/people/customers')}
              >
                Add New Credit Customer
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Paper>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>S.No</TableCell>
                      <TableCell>ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Balance Due</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCustomers
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((customer, index) => (
                        <TableRow key={customer.id}>
                          <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                          <TableCell>{customer.id}</TableCell>
                          <TableCell>{customer.name}</TableCell>
                          <TableCell>{customer.phone}</TableCell>
                          <TableCell>{customer.email}</TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                              {parseFloat(customer.balanceDue || 0) > 0 ? (
                                <>
                                  <WalletIcon color="error" fontSize="small" />
                                  <Typography color="error.main" fontWeight="bold">
                                    {formatCurrency(customer.balanceDue)}
                                  </Typography>
                                </>
                              ) : parseFloat(customer.balanceDue || 0) === 0 ? (
                                <>
                                  <WalletIcon color="success" fontSize="small" />
                                  <Typography color="success.main">
                                    {formatCurrency(customer.balanceDue)}
                                  </Typography>
                                </>
                              ) : (
                                <>
                                  <WalletIcon color="primary" fontSize="small" />
                                  <Typography color="primary.main">
                                    {formatCurrency(customer.balanceDue)} (Credit)
                                  </Typography>
                                </>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              endIcon={<MoreVertIcon />}
                              onClick={(event) => handleOpenMenu(event, customer.id)}
                            >
                              Actions
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredCustomers.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />

              {/* Actions Menu */}
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem onClick={handlePayAction}>
                  <ListItemIcon>
                    <PaymentIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Pay" />
                </MenuItem>
                <MenuItem onClick={handleLedgerAction}>
                  <ListItemIcon>
                    <LedgerIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Ledger" />
                </MenuItem>
              </Menu>
            </>
          )}
        </Paper>
      </Box>

      {/* Payment Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={handleClosePaymentDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h5" component="div">Process Payment</Typography>
          <IconButton
            onClick={handleClosePaymentDialog}
            size="small"
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {selectedCustomer && (
            <Grid container spacing={3}>
              {/* Customer Info Section */}
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="h6" gutterBottom>
                    Customer Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body1">
                        <strong>Name:</strong> {selectedCustomer.name}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Phone:</strong> {selectedCustomer.phone}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>Total Sale:</strong> {formatCurrency(creditSalesData.totalSale)}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>Total Paid:</strong> {formatCurrency(creditSalesData.totalPaid)}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>Current Balance:</strong> {formatCurrency(creditSalesData.currentBalance)}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Opening Balance:</strong> {formatCurrency(selectedCustomer.openingBalance || 0)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Payment Details */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Payment Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      id="payment-amount-input"
                      name="payment-amount"
                      fullWidth
                      required
                      label="Payment Amount"
                      value={paymentAmount}
                      onChange={handlePaymentAmountChange}
                      placeholder="Enter payment amount"
                      type="number"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            {getCurrencySymbol()}
                          </InputAdornment>
                        ),
                        inputProps: {
                          'aria-label': 'Payment amount',
                          min: 0,
                          max: creditSalesData.currentBalance,
                          step: "0.01"
                        }
                      }}
                      error={parseFloat(paymentAmount) > creditSalesData.currentBalance}
                      helperText={
                        parseFloat(paymentAmount) > creditSalesData.currentBalance
                          ? "Amount cannot exceed current balance"
                          : ""
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      id="payment-date-input"
                      name="payment-date"
                      fullWidth
                      required
                      label="Payment Date"
                      type="date"
                      value={paymentDate}
                      onChange={(e) => setPaymentDate(e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        'aria-label': 'Payment date'
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      id="payment-method-input"
                      name="payment-method"
                      fullWidth
                      required
                      select
                      label="Payment Method"
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      inputProps={{
                        'aria-label': 'Payment method'
                      }}
                    >
                      <MenuItem value="Cash">Cash</MenuItem>
                      <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                      <MenuItem value="Cheque">Cheque</MenuItem>
                      <MenuItem value="PayNow">UPI</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      id="payment-note-input"
                      name="payment-note"
                      fullWidth
                      label="Payment Note"
                      value={paymentNote}
                      onChange={(e) => setPaymentNote(e.target.value)}
                      multiline
                      rows={2}
                      placeholder="Add any additional notes here"
                      inputProps={{
                        'aria-label': 'Payment note'
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClosePaymentDialog}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitPayment}
            disabled={!paymentAmount || parseFloat(paymentAmount) <= 0 || parseFloat(paymentAmount) > creditSalesData.currentBalance || processingPayment}
            color="primary"
            startIcon={processingPayment ? <CircularProgress size={20} /> : null}
          >
            {processingPayment ? 'Processing...' : 'Process Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View All Transactions Dialog */}
      <Dialog
        open={allTransactionsDialogOpen}
        onClose={handleCloseAllTransactionsDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, height: '80vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h5" component="div">
            {showingHistoryLedger ? `Ledger - ${selectedHistoryCustomer && selectedHistoryCustomer.name}` : 'All Customers with Credit History'}
          </Typography>
          <IconButton
            onClick={handleCloseAllTransactionsDialog}
            size="small"
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {!showingHistoryLedger ? (
            // Customer Selection View
            <Box sx={{ p: 3 }}>
              {loadingAllCustomers ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <CircularProgress />
                  <Typography sx={{ ml: 2 }}>Loading customers with credit history...</Typography>
                </Box>
              ) : (
                <>
                  <Typography variant="h6" gutterBottom>
                    Select a customer to view their complete credit transaction history:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {historySearchTerm ?
                      `Showing ${filteredHistoryCustomers.length} of ${allCustomersWithHistory.length} customers` :
                      `Found ${allCustomersWithHistory.length} customers who have had credit transactions`
                    }
                  </Typography>

                  {/* Search Input */}
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Search Customers"
                    value={historySearchTerm}
                    onChange={handleHistorySearchChange}
                    InputProps={{
                      startAdornment: (
                        <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      ),
                    }}
                    placeholder="Search by customer name..."
                    size="small"
                    sx={{ mb: 3 }}
                  />

                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>S.No</TableCell>
                          <TableCell>Customer Name</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Phone</TableCell>
                          <TableCell>Current Balance Due</TableCell>
                          <TableCell>Total Credit Sales</TableCell>
                          <TableCell>Total Payments</TableCell>
                          <TableCell>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredHistoryCustomers.map((customer, index) => (
                          <TableRow key={customer.id} hover>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>{customer.name}</TableCell>
                            <TableCell>{customer.email}</TableCell>
                            <TableCell>{customer.phone}</TableCell>
                            <TableCell align="right">
                              {formatCurrency(customer.balanceDue)}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(customer.totalCreditSales)}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(customer.totalPayments)}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleSelectHistoryCustomer(customer)}
                                startIcon={<LedgerIcon />}
                              >
                                View Ledger
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          ) : (
            // Customer Ledger View
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Button
                  variant="outlined"
                  onClick={handleBackToCustomerList}

                  sx={{ mr: 2 }}
                >
                  Back to Customer List
                </Button>
                <Typography variant="h6">
                  Credit Transaction History for {selectedHistoryCustomer && selectedHistoryCustomer.name}
                </Typography>
              </Box>

              {/* Customer Summary Cards */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" color="primary">Opening Balance</Typography>
                      <Typography variant="h5">{formatCurrency((selectedHistoryCustomer && selectedHistoryCustomer.openingBalance) || 0)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" color="info.main">Total Credit Sales</Typography>
                      <Typography variant="h5">{formatCurrency((selectedHistoryCustomer && selectedHistoryCustomer.totalCreditSales) || 0)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" color="success.main">Total Payments</Typography>
                      <Typography variant="h5">{formatCurrency((selectedHistoryCustomer && selectedHistoryCustomer.totalPayments) || 0)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" color={(selectedHistoryCustomer && selectedHistoryCustomer.balanceDue > 0) ? "error.main" : "success.main"}>
                        Current Balance Due
                      </Typography>
                      <Typography variant="h5">{formatCurrency((selectedHistoryCustomer && selectedHistoryCustomer.balanceDue) || 0)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Customer Details */}
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography><strong>Customer ID:</strong> {selectedHistoryCustomer && selectedHistoryCustomer.id}</Typography>
                    <Typography><strong>Email:</strong> {selectedHistoryCustomer && selectedHistoryCustomer.email}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography><strong>Phone:</strong> {selectedHistoryCustomer && selectedHistoryCustomer.phone}</Typography>
                    <Typography><strong>Contact Person:</strong> {selectedHistoryCustomer && selectedHistoryCustomer.contactPerson}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Transaction Table */}
              <Paper variant="outlined">
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6">Transaction History</Typography>
                </Box>
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Complete transaction history will be loaded here via Customer Ledger page integration
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate(`/customer-credit-ledger/${selectedHistoryCustomer && selectedHistoryCustomer.id}`)}
                    startIcon={<LedgerIcon />}
                    sx={{ mt: 2 }}
                  >
                    Open Full Ledger View
                  </Button>
                </Box>
              </Paper>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          {showingHistoryLedger && (
            <Button onClick={handleBackToCustomerList} variant="outlined">
              Back to Customer List
            </Button>
          )}
          <Button onClick={handleCloseAllTransactionsDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default CreditCustomersReport; 